import { get, post } from '../utils/apiUtils';

const TEAM_RUN_URL = `/api/v1/mms_ai_metric_events/metrics/runs/pipeline`;
const OBSERVIBILITY_COUNT_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_count`;
const TOTAL_RUNS_COUNT_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_total_count`;
const METRIC_RUN_BY_ID_URL = `/api/v1/mms_ai_metric_events/metrics/runs/by_id`;
const METRIC_CHILD_RUNS_URL = `/api/v1/mms_ai_metric_events/metrics/runs/child_runs`;
const TOTAL_TOKENS_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_total_tokens`;
const TOTAL_TOKENS_PCT_CHANGE_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_total_tokens_percent_change_last_24h`;
const RUN_COUNT_PCT_CHANGE_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_run_count_percent_change_last_24h`;
const SUCCESS_RATE_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_success_rate`;
const SUCCESS_RATE_PCT_CHANGE_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_success_rate_percent_change_last_24h`;
const FAILED_RUNS_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_failed_runs`;
const FAILED_RUNS_PCT_CHANGE_URL = `/api/v1/mms_ai_metric_events/metrics/runs/get_failed_runs_percent_change_last_24h`;
const METRIC_SPLIT_BASE_URL = `/api/v1/mms_ai_metric_events/metrics/runs`;

const PIPELINE_CONFIG_URL = `/api/v1/mms_ai_configs/pipeline/config`;
const PIPELINE_CONFIG_paginated_URL = `/api/v1/mms_ai_metric_events/metrics/runs/configs_with_aggregate_metrics`;
const API_URL = `/api/v1/mms_persistence`;



/**
 * fetch pipelines runs
 * @returns {Promise<Object>} Promise resolving to waiting task
 */
export const getPipelinesRun = async (payload) => {
  try {
    const response = await post(`${TEAM_RUN_URL}`, payload);
    return response;
  } catch (error) {
    console.error('Error updating system settings:', error);
    throw error;
  }
};

/**
 * Fetch a single metric run by numeric id
 * @param {string|number} id - Metric run primary key id
 * @returns {Promise<Object>} Promise resolving to metric run
 */
export const getMetricRunById = async (id) => {
  if (!id && id !== 0) throw new Error('Metric run id is required');
  try {
    const response = await get(`${METRIC_RUN_BY_ID_URL}/${id}`);
    return response;
  } catch (error) {
    console.error('Error getting metric run by id:', error);
    throw error;
  }
};

/**
 * Fetch child metric runs (e.g. agent/planner runs) for a parent pipeline run.
 * @param {string} parentRunId - The parent pipeline run_id
 * @param {Object} payload - Fetch request body with filter/skip/limit
 * @returns {Promise<Array|Object>} Promise resolving to list of child metric runs (or API payload containing them)
 */
export const getChildMetricRuns = async (parentRunId, payload) => {
  if (!parentRunId) throw new Error('Parent run id is required');
  const body = payload || { filter: {}, skip: 0, limit: 50 };
  try {
    const response = await post(`${METRIC_CHILD_RUNS_URL}/${parentRunId}`, body);
    return response;
  } catch (error) {
    console.error('Error getting child metric runs:', error);
    throw error;
  }
};


/**
 * Get all pipelines with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
export const getPipelinesByPagination = async (filter = {}, sort = {}, query = {}) => {
  try {
    const {limit = 10, skip = 0} = query;
    const response = await post(`${PIPELINE_CONFIG_paginated_URL}/pipeline?`, { filter, sort, limit, skip });
    return response;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};


/**
 * Get pipeline count
 * @param {string} pipelineId - Pipeline execution ID
 * @returns {Promise<Object>} Promise resolving to pipeline execution status
 */
export const getEntityCount = async (entity) => {
   try {
     const response = await post(`${API_URL}/${entity}/count`, {});
     return response.count;
   } catch (error) {
     console.error('Error fetching knowledge bases:', error);
     throw error;
   }
};


/**
 * fetch pipelines runs count
 * @returns {Promise<Object>} Promise resolving to waiting task
 */
export const getObservabilityCount = async (entityName, filter = {}) => {
  try {
    const response = await post(`${OBSERVIBILITY_COUNT_URL}/${entityName}`, filter);
    return response?.count;
  } catch (error) {
    console.error('Error getting observability count:', error);
    throw error;
  }
};

/**
 * Fetch total tokens across all metric runs.
 * @returns {Promise<number>} total token count
 */
export const getTotalTokens = async (filter = {}) => {
  try {
    const response = await post(TOTAL_TOKENS_URL, filter);
    return typeof response?.total_count === 'number' ? response.total_count : 0;
  } catch (error) {
    console.error('Error getting total tokens:', error);
    throw error;
  }
};

/**
 * Fetch total tokens percent change for the last 24 hours.
 * @returns {Promise<{current_total:number,previous_total:number,percent_change:number|null}>}
 */
export const getTotalTokensPercentChangeLast24h = async (filter = {}) => {
  try {
    const response = await post(TOTAL_TOKENS_PCT_CHANGE_URL, filter);
    return response || { current_total: 0, previous_total: 0, percent_change: null };
  } catch (error) {
    console.error('Error getting total tokens percent change (last 24h):', error);
    throw error;
  }
};

/**
 * Fetch total run count percent change for the last 24 hours.
 * @returns {Promise<{current_count:number,previous_count:number,percent_change:number|null}>}
 */
export const getRunCountPercentChangeLast24h = async (filter = {}) => {
  try {
    const response = await post(RUN_COUNT_PCT_CHANGE_URL, filter);
    return response || { current_count: 0, previous_count: 0, percent_change: null };
  } catch (error) {
    console.error('Error getting run count percent change (last 24h):', error);
    throw error;
  }
};

/**
 * Fetch total metric runs count across all types (pipeline, planner, agent, etc.).
 * @returns {Promise<number>} total runs count
 */
export const getTotalRunsCount = async (filter = {}) => {
  try {
    const response = await post(TOTAL_RUNS_COUNT_URL, filter);
    return typeof response?.count === 'number' ? response.count : 0;
  } catch (error) {
    console.error('Error getting total runs count:', error);
    throw error;
  }
};

/**
 * Fetch overall success rate across all metric runs.
 * @returns {Promise<{success_rate:number}>}
 */
export const getSuccessRate = async (filter = {}) => {
  try {
    const response = await post(SUCCESS_RATE_URL, filter);
    return response || { success_rate: 0 };
  } catch (error) {
    console.error('Error getting success rate:', error);
    throw error;
  }
};

/**
 * Fetch success rate percent change for the last 24 hours.
 * @returns {Promise<{current_success_rate:number,previous_success_rate:number,percent_change:number|null}>}
 */
export const getSuccessRatePercentChangeLast24h = async (filter = {}) => {
  try {
    const response = await post(SUCCESS_RATE_PCT_CHANGE_URL, filter);
    return response || { current_success_rate: 0, previous_success_rate: 0, percent_change: null };
  } catch (error) {
    console.error('Error getting success rate percent change (last 24h):', error);
    throw error;
  }
};

/**
 * Fetch overall failed runs count across all metric runs.
 * @returns {Promise<{failed_count:number}>}
 */
export const getFailedRuns = async (filter = {}) => {
  try {
    const response = await post(FAILED_RUNS_URL, filter);
    return response || { failed_count: 0 };
  } catch (error) {
    console.error('Error getting failed runs:', error);
    throw error;
  }
};

/**
 * Fetch failed runs percent change for the last 24 hours.
 * @returns {Promise<{current_failed_count:number,previous_failed_count:number,percent_change:number|null}>}
 */
export const getFailedRunsPercentChangeLast24h = async (filter = {}) => {
  try {
    const response = await post(FAILED_RUNS_PCT_CHANGE_URL, filter);
    return response || { current_failed_count: 0, previous_failed_count: 0, percent_change: null };
  } catch (error) {
    console.error('Error getting failed runs percent change (last 24h):', error);
    throw error;
  }
};

export const getTokenSplit = async (metricType, metricCode, filter = {}) => {
  if (!metricType || !metricCode) throw new Error('metricType and metricCode are required');
  try {
    const url = `${METRIC_SPLIT_BASE_URL}/token_split/${metricType}/${metricCode}`;
    const response = await post(url, filter || {});
    return response;
  } catch (error) {
    console.error('Error getting token split:', error);
    throw error;
  }
};

export const getCountSplit = async (metricType, metricCode, filter = {}) => {
  if (!metricType || !metricCode) throw new Error('metricType and metricCode are required');
  try {
    const url = `${METRIC_SPLIT_BASE_URL}/count_split/${metricType}/${metricCode}`;
    const response = await post(url, filter || {});
    return response;
  } catch (error) {
    console.error('Error getting count split:', error);
    throw error;
  }
};

export const getAvgDurationSplit = async (metricType, metricCode, filter = {}) => {
  if (!metricType || !metricCode) throw new Error('metricType and metricCode are required');
  try {
    const url = `${METRIC_SPLIT_BASE_URL}/avg_duration_split/${metricType}/${metricCode}`;
    const response = await post(url, filter || {});
    return response;
  } catch (error) {
    console.error('Error getting avg duration split:', error);
    throw error;
  }
};





